/**
 * Vocabulário de tela do grupo — o que cada estado SIGNIFICA para quem opera.
 *
 * Fica separado da tela pelo mesmo motivo do `monitor-ui.ts`: o teste afirma sobre os
 * rótulos sem montar a página, e lista, cartão e diálogo usam o mesmo texto.
 *
 * Nada aqui decide regra de negócio. O servidor já mandou `status`, a contagem e o limite
 * do plano avaliado. Isto é tradução, não cálculo (invariantes 3 e 4).
 */
import type { Group } from '../../lib/api';

export type GroupStatus = Group['status'];

/** Os três estados, na ordem em que contam a história do grupo. */
export const GROUP_STATUSES: readonly GroupStatus[] = ['ACTIVE', 'FULL', 'ARCHIVED'];

export interface GroupStatusSpec {
  label: string;
  tone: 'success' | 'warning' | 'neutral';
  hint: string;
}

/**
 * `FULL` não quer dizer que o WhatsApp recusou alguém — quer dizer que **nós** paramos de
 * mandar gente. A frase precisa deixar isso claro, senão a primeira pergunta de quem vê a
 * tela é "mas ainda cabe, por que parou?".
 */
export const GROUP_STATUS: Record<GroupStatus, GroupStatusSpec> = {
  ACTIVE: {
    label: 'Recebendo',
    tone: 'success',
    hint: 'O grupo está no rodízio e pode receber gente nova.',
  },
  FULL: {
    label: 'Cheio',
    tone: 'warning',
    hint: 'Bateu o limite de segurança e saiu do rodízio. Ainda cabe gente no WhatsApp — a margem existe porque a contagem é sempre um retrato atrasado.',
  },
  /**
   * A coluna se chama `deleted_at` e o estado se chama `ARCHIVED` — nomes de
   * implementação. Quem lê a tela não arquiva nada: **deixa de monitorar**. O produto
   * falava as duas línguas (o diálogo de escolha dizia "Deixar de monitorar", a lista
   * dizia "Arquivar") e o dono, olhando a mesma tela, não achou o caminho.
   */
  ARCHIVED: {
    label: 'Não monitorado',
    tone: 'neutral',
    hint: 'Você deixou de monitorar este grupo. Ele continua no WhatsApp, o histórico fica guardado, e dá para voltar a monitorar quando quiser.',
  },
};

/* -------------------------------------------------------------------------- */
/*  Uma língua só: o que cada gesto se chama                                   */
/* -------------------------------------------------------------------------- */

/**
 * Os rótulos dos três gestos, escritos **uma vez**.
 *
 * Existem como constante e não soltos na tela porque foi exatamente a duplicata que criou
 * o problema: o mesmo ato tinha dois nomes em duas telas, e o teste não tinha como afirmar
 * que eram o mesmo.
 */
export const GROUP_ACTION = {
  stop: 'Deixar de monitorar',
  resume: 'Voltar a monitorar',
  remove: 'Excluir',
} as const;

/* -------------------------------------------------------------------------- */
/*  Excluir: o que ainda segura o registro                                     */
/* -------------------------------------------------------------------------- */

/** Os fatos que o servidor mede; `null` em grupo na operação = **não medido**. */
export type GroupRemovalFacts = Pick<
  Group,
  'archivedAt' | 'hasHistory' | 'campaignLinks' | 'hasTraffic'
>;

/**
 * Por que a exclusão definitiva ainda NÃO está disponível — ou `null` quando está.
 *
 * É o mesmo critério de `monitorRemovalBlocker`, de propósito: um produto que explica a
 * mesma recusa de dois jeitos obriga a pessoa a aprender duas vezes.
 *
 *  1. **Deixar de monitorar vem antes.** `removeGroup` recusa com `CONFLICT` quem ainda
 *     está na operação. O passo a mais existe para dar chance de perceber o engano — e
 *     aqui, ao contrário do monitorador, voltar atrás existe ("Voltar a monitorar").
 *  2. **Histórico segura o registro.** `participant_events.group_id`,
 *     `campaign_groups.group_id`, `redirect_clicks.allocated_group_id` e
 *     `attribution_sessions.target_group_id` são FK `onDelete: 'restrict'`. Enquanto houver
 *     qualquer um deles, o `DELETE` é recusado pelo **banco** — e o que chegava na tela era
 *     um erro de servidor sem explicação.
 *
 * O que sobra é o caso legítimo: o grupo importado por engano, que **nunca capturou um
 * único evento**. Esse não precisa virar arquivo morto permanente.
 *
 * `hasHistory`/`campaignLinks`/`hasTraffic` vêm `null` em grupo na operação porque o
 * servidor não os mede ali (o botão nem aparece). `null` não é `false`: por isso o primeiro
 * `if` é o que trata esse caso, e nenhum dos seguintes lê um `null` como "não tem".
 */
export function groupRemovalBlocker(group: GroupRemovalFacts): string | null {
  if (group.archivedAt === null) {
    return 'Deixe de monitorar antes de excluir: é o passo que dá chance de perceber o engano.';
  }

  const presos: string[] = [];
  if (group.hasHistory === true) presos.push('histórico de entradas e saídas');
  if (group.campaignLinks !== null && group.campaignLinks > 0) {
    presos.push(
      group.campaignLinks === 1
        ? 'vínculo com 1 campanha'
        : `vínculo com ${group.campaignLinks} campanhas`,
    );
  }
  if (group.hasTraffic === true) presos.push('cliques já distribuídos para ele');
  if (presos.length === 0) return null;

  return `Não dá para excluir: ainda há ${presos.join(' e ')} neste grupo. Sem monitorar ele não ocupa vaga do plano e o histórico fica de pé.`;
}

export function groupStatusLabel(status: GroupStatus): string {
  return GROUP_STATUS[status].label;
}

/**
 * Nome de grupo que o provider não mandou.
 *
 * `null` aqui é "o WhatsApp não nos disse o nome", e não "grupo sem nome" — por isso o
 * texto é uma explicação, e não um travessão solto (invariante 8).
 */
export const GROUP_WITHOUT_NAME = 'Sem nome no WhatsApp';

export function groupDisplayName(group: Pick<Group, 'name' | 'providerGroupId'>): string {
  return group.name ?? GROUP_WITHOUT_NAME;
}

/**
 * Lotação: quanto do **limite de segurança** já foi ocupado.
 *
 * Contra `safetyThreshold`, jamais contra `maxParticipants`. Medir contra a capacidade
 * mostraria "76% cheio" num grupo que para nós já fechou — e é assim que se perde entrada.
 *
 * `null` quando nunca houve sincronização: sem contagem não há percentual, e inventar `0%`
 * diria que o grupo está vazio.
 */
export function occupancy(
  group: Pick<Group, 'participantsCount' | 'safetyThreshold'>,
): number | null {
  if (group.participantsCount === null) return null;
  if (group.safetyThreshold <= 0) return null;
  return Math.min(1, group.participantsCount / group.safetyThreshold);
}

/** Vagas até o limite de segurança. `null` sem contagem — não `0`. */
export function seatsLeft(
  group: Pick<Group, 'participantsCount' | 'safetyThreshold'>,
): number | null {
  if (group.participantsCount === null) return null;
  return Math.max(0, group.safetyThreshold - group.participantsCount);
}

/**
 * Tom da barra de lotação. Três faixas, e a do meio existe para dar tempo de agir: quem só
 * descobre que o grupo encheu quando ele encheu já perdeu as entradas do caminho.
 */
export function occupancyTone(value: number | null): 'success' | 'warning' | 'danger' | 'muted' {
  if (value === null) return 'muted';
  if (value >= 1) return 'danger';
  if (value >= 0.85) return 'warning';
  return 'success';
}

/** Frase da lotação para leitor de tela — a cor da barra não é canal único de informação. */
export function occupancyLabel(
  group: Pick<Group, 'participantsCount' | 'safetyThreshold'>,
): string {
  if (group.participantsCount === null) {
    return 'Sem contagem ainda — este grupo nunca foi sincronizado.';
  }
  const left = seatsLeft(group) ?? 0;
  if (left === 0) {
    return `${group.participantsCount} de ${group.safetyThreshold} do limite de segurança. Sem vagas.`;
  }
  return `${group.participantsCount} de ${group.safetyThreshold} do limite de segurança. ${left} ${
    left === 1 ? 'vaga' : 'vagas'
  }.`;
}

/** O que a importação fez, em uma frase. Serve ao toast e ao teste. */
export function importSummary(result: {
  imported: number;
  updated: number;
  skippedByLimit: number;
  seen: number;
}): string {
  const partes: string[] = [];
  if (result.imported > 0) {
    partes.push(`${result.imported} ${result.imported === 1 ? 'grupo novo' : 'grupos novos'}`);
  }
  if (result.updated > 0) {
    partes.push(`${result.updated} ${result.updated === 1 ? 'atualizado' : 'atualizados'}`);
  }
  if (partes.length === 0) {
    return result.seen === 0
      ? 'Este número não está em nenhum grupo.'
      : 'Nada mudou desde a última vez.';
  }
  return partes.join(' e ') + '.';
}

/* -------------------------------------------------------------------------- */
/*  Varredura de participantes: saber QUEM, não só quantos                     */
/* -------------------------------------------------------------------------- */

/**
 * `lastSyncedAt` e `participantsSyncedAt` respondem perguntas diferentes, e a tela precisa
 * dizer qual das duas está respondida.
 *
 * A primeira é a varredura de GRUPOS: quantos são. A segunda é a varredura de
 * PARTICIPANTES: quem são. Um grupo com 1218 pessoas e `participantsSyncedAt: null` foi o
 * estado real da produção — a contagem existia e a lista de pessoas estava vazia.
 */
export function participantsKnown(group: Pick<Group, 'participantsSyncedAt'>): boolean {
  return group.participantsSyncedAt !== null;
}

export const PARTICIPANTS_NEVER_SYNCED =
  'Ainda não sabemos QUEM está neste grupo — só quantos. Sincronize as pessoas para o painel conseguir acompanhar entrada e saída de cada uma.';

/** O que a varredura fez, em uma frase. Serve ao toast e ao teste. */
export function syncSummary(result: {
  mode: 'baseline' | 'reconcile';
  seen: number;
  discovered: number;
  missing: number;
  participantsCount: number;
  divergence: number | null;
  untrusted: boolean;
}): string {
  /**
   * O caso que não pode virar comemoração: o WhatsApp não devolveu ninguém e nós NÃO
   * aplicamos a diferença. Dizer "sincronizado" aqui seria a tela mentindo por omissão.
   */
  if (result.untrusted) {
    return 'O WhatsApp não devolveu nenhum participante, e não confiamos nessa resposta num grupo que tem gente. Nada foi alterado — tente de novo em alguns minutos.';
  }

  if (result.mode === 'baseline') {
    return `Agora sabemos quem são as ${result.participantsCount} pessoas deste grupo.`;
  }

  const partes: string[] = [];
  if (result.discovered > 0) {
    partes.push(
      result.discovered === 1
        ? '1 pessoa que tinha entrado sem a gente saber'
        : `${result.discovered} pessoas que tinham entrado sem a gente saber`,
    );
  }
  if (result.missing > 0) {
    partes.push(
      result.missing === 1
        ? '1 que tinha saído sem a gente saber'
        : `${result.missing} que tinham saído sem a gente saber`,
    );
  }

  if (partes.length === 0) return `Tudo certo: ${result.participantsCount} pessoas, e nada mudou.`;
  return `Achamos ${partes.join(' e ')}. A contagem foi refeita: ${result.participantsCount} pessoas.`;
}

/* -------------------------------------------------------------------------- */
/*  Convite: a única coisa que decide se o grupo recebe gente                   */
/* -------------------------------------------------------------------------- */

/**
 * **Grupo sem convite não é um detalhe: é um grupo que nunca receberá ninguém.**
 *
 * O redirecionador só manda gente para grupo que tenha `invite_code` gravado (a cláusula
 * `g.invite_code is not null and g.invite_revoked_at is null` do allocator). Sem ele o grupo
 * é pulado **em silêncio** — a lista mostra o grupo ativo, com vaga, aparentemente em dia, e
 * nenhum clique chega nele.
 *
 * Guardamos só o CÓDIGO, nunca a URL (invariante 7): o destino é montado no servidor.
 */
export function hasInvite(group: Pick<Group, 'inviteCode'>): boolean {
  return group.inviteCode !== null && group.inviteCode.length > 0;
}

export const INVITE_MISSING =
  'Este grupo está sem link de convite, então nenhum redirecionador consegue mandar gente para ele — quem clicar vai para outro grupo ou para o destino de reserva. Busque o convite para ele voltar a receber.';

export const INVITE_PRESENT =
  'Temos o convite deste grupo guardado. É por ele que quem clica num link seu entra aqui.';

/**
 * Por que existe um botão de **rebuscar** um convite que já temos.
 *
 * O convite morre por fora: quem administra o grupo revoga o link pelo celular quando quiser
 * e o WhatsApp não nos avisa. A partir dali o grupo some da distribuição sem nenhum erro na
 * tela — e o único sintoma é a entrada que parou de acontecer.
 */
export const INVITE_WHY_REFRESH =
  'O convite pode ter sido trocado ou revogado no celular sem ninguém avisar. Buscar de novo confirma se o que temos guardado ainda é o que vale.';

export interface InviteRefreshOutcome {
  changed: boolean;
  previousCode: string | null;
}

export interface InviteRefreshSummary {
  title: string;
  description: string;
  tone: 'success' | 'warning';
}

/**
 * O resultado do rebuscar, **em língua de gente**.
 *
 * `changed: false` é BOA NOTÍCIA — quer dizer "o convite continua o mesmo" —, e tratá-lo como
 * fracasso ("nada foi feito", tom de erro) ensinaria a pessoa a clicar de novo procurando um
 * sucesso que já aconteceu. Os três desfechos são diferentes e merecem três frases:
 *
 *  - não mudou: estava certo o tempo todo;
 *  - mudou e não havia nada antes: o grupo acabou de passar a receber gente;
 *  - mudou e havia outro: o antigo tinha sido revogado, e quem o tivesse não entraria mais.
 */
export function inviteRefreshSummary(result: InviteRefreshOutcome): InviteRefreshSummary {
  if (!result.changed) {
    return {
      title: 'O convite continua o mesmo',
      description:
        'Nada mudou no WhatsApp: o convite que já estava guardado continua valendo, e o grupo segue recebendo gente normalmente.',
      tone: 'success',
    };
  }

  if (result.previousCode === null) {
    return {
      title: 'Convite guardado',
      description:
        'Este grupo não tinha convite e agora tem: a partir de agora ele entra no rodízio e pode receber quem clicar nos seus links.',
      tone: 'success',
    };
  }

  return {
    title: 'O convite tinha mudado',
    description:
      'O convite antigo não valia mais — foi trocado ou revogado no WhatsApp. Guardamos o novo, e o grupo volta a receber gente.',
    tone: 'success',
  };
}

/**
 * D4: "Admins 3 · Membros 412". `null` quando o servidor ainda não sabe QUEM está no grupo
 * (sem varredura completa) — aí a tela não inventa "0 admins" (invariante 8).
 */
export function adminsMembersLabel(
  group: Pick<Group, 'adminsCount' | 'membersCount'>,
): string | null {
  if (group.adminsCount === null || group.membersCount === null) return null;
  const n = (v: number) => v.toLocaleString('pt-BR');
  return `Admins ${n(group.adminsCount)} · Membros ${n(group.membersCount)}`;
}
